/**
 * What a run is doing, in the words the catalog shows and the console prints.
 *
 * The pipeline reports through one callback — `(stageId, current, total)`,
 * osc-cpp/wasm/ABI.md — and step 10 is the only stage that counts anything:
 * it fires per staff, `(i, N)`, which is what "staff 7 of 12" is read from.
 * The other four fire once each, so between them the only honest thing to show
 * is their name.
 *
 * The weights are the ones the ABI publishes for drawing a single bar. They
 * are what makes an estimate possible at all: a page's stages are wildly
 * uneven — step 10 is most of the run — so elapsed/fraction extrapolates far
 * better than counting stages would.
 */

export const STAGES: Record<number, { label: string; weight: number }> = {
  1: { label: 'preprocessing', weight: 0.03 },
  2: { label: 'segmenting', weight: 0.12 },
  3: { label: 'finding staves', weight: 0.10 },
  4: { label: 'reading staves', weight: 0.70 },
  5: { label: 'writing MusicXML', weight: 0.05 },
};

/** How much of a page is behind us, 0..1, from the last event it reported. */
export function barFraction(stage: number, current: number, total: number): number {
  let done = 0;
  for (const [id, { weight }] of Object.entries(STAGES)) {
    if (Number(id) < stage) done += weight;
  }
  const own = STAGES[stage]?.weight ?? 0;
  // Step 10 announces itself with (0, 0) before it knows the staff count; the
  // one-shot stages report (1, 1), which is the same arithmetic.
  const within = total > 0 ? Math.min(1, current / total) : 0;
  return Math.min(1, done + own * within);
}

/** "1:03", "12s", "1:02:03" — short enough for a badge. */
export function formatClock(secs: number): string {
  const total = Math.max(0, Math.round(secs));
  if (total < 60) return `${total}s`;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const mm = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes);
  return `${hours > 0 ? `${hours}:` : ''}${mm}:${String(seconds).padStart(2, '0')}`;
}

/** Everything the badge knows about a run in flight. */
export interface RunState {
  /** 1-based; a single image is page 1 of 1. */
  page: number;
  pages: number;
  threads?: number;
  stage?: number;
  current?: number;
  total?: number;
  /** Seconds the current page has been running. */
  elapsed: number;
  /**
   * `elapsed` as it stood when the pipeline last said something. The estimate
   * is projected from that instant and then counted down, so a stage that goes
   * quiet — step 10 on a dense staff — leaves "time left" falling rather than
   * climbing, which is what projecting from a growing `elapsed` would do.
   */
  markedElapsed?: number;
  /** Seconds each finished page of this document took. */
  finished: number[];
}

/**
 * Seconds left, or null while there is nothing to extrapolate from: the first
 * few percent of a page make for an estimate that swings by minutes, and a
 * number that lies is worse than no number.
 *
 * Pages after the first lean on the ones already measured; the first page has
 * only its own fraction to go on.
 */
export function estimateLeft(state: RunState): number | null {
  const fraction = state.stage === undefined
    ? 0
    : barFraction(state.stage, state.current ?? 0, state.total ?? 0);
  const measured = state.finished.length > 0
    ? state.finished.reduce((sum, secs) => sum + secs, 0) / state.finished.length
    : null;

  let thisPage: number | null = null;
  if (fraction >= 0.05) {
    const projected = (state.markedElapsed ?? state.elapsed) / fraction;
    thisPage = Math.max(0, projected - state.elapsed);
  } else if (measured !== null) {
    thisPage = Math.max(0, measured - state.elapsed);
  }
  if (thisPage === null) return null;

  const rest = state.pages - state.page;
  if (rest <= 0) return thisPage;
  // A page not started yet is worth a measured page, or this one's own
  // projection when nothing has finished.
  const perPage = measured ?? state.elapsed + thisPage;
  return thisPage + rest * perPage;
}

/**
 * The badge line: what it is doing, how wide it is running, how long this page
 * has taken and what is left. Parts are dropped rather than shown empty — a
 * run that has not reported a stage yet says so by saying less.
 */
export function statusLine(state: RunState): string {
  const parts: string[] = [];
  if (state.pages > 1) parts.push(`page ${state.page}/${state.pages}`);

  if (state.stage === 4 && (state.total ?? 0) > 0) {
    parts.push(`staff ${state.current}/${state.total}`);
  } else if (state.stage !== undefined) {
    parts.push(STAGES[state.stage]?.label ?? `stage ${state.stage}`);
  }

  if (state.threads !== undefined) parts.push(`${state.threads} threads`);
  parts.push(formatClock(state.elapsed));

  const left = estimateLeft(state);
  if (left !== null) parts.push(`~${formatClock(left)} left`);
  return parts.join(' · ');
}

/**
 * The console lines. The thread count is the one `osc_set_threads` returned,
 * not the one it was asked for — they differ when the module clamps, and a run
 * that quietly fell back to one thread looks, from outside, like a slow
 * machine. The start line is written before the pipeline blocks its worker,
 * because a page takes minutes and a number that arrives afterwards answers a
 * question nobody is still asking.
 */
export function startedLine(
  title: string,
  threads: number,
  decodeUnits: number,
  asked: number,
): string {
  const clamped = threads === asked ? '' : ` (asked for ${asked})`;
  return `recognition: ${title} — running on ${threads} thread(s)${clamped}, ` +
    `${decodeUnits} decode unit(s) in flight`;
}

export function finishedLine(title: string, threads: number, secs: number): string {
  return `recognition: ${title} — done on ${threads} thread(s) in ${secs.toFixed(1)} s`;
}
